import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';

export function initSentry() {
  if (!SENTRY_DSN) return;

  const explicitRelease = import.meta.env.VITE_SENTRY_RELEASE;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    // Omit `release` unless explicitly set so @sentry/vite-plugin can inject
    // the git/SHA release it uploaded source maps for.
    ...(explicitRelease ? { release: explicitRelease } : {}),

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],

    // Attach IP and request user data so session replay can be filtered by person.
    sendDefaultPii: true,

    // Performance monitoring — sample 20% of transactions in production
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.2 : 1.0,

    // Session replay — capture 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Don't send errors in development unless DSN is explicitly set
    enabled: Boolean(SENTRY_DSN),

    // Ignore common non-actionable errors
    ignoreErrors: [
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'AbortError',
      /The play\(\) request was interrupted/,
      /Failed to read the 'localStorage' property from 'Window'/,
      /Loading chunk [\d]+ failed/,
      /Failed to fetch dynamically imported module/,
      // Apollo 3.11+ throws this when fetchMore runs on a no-cache query
      // without updateQuery. Production minifies it to error code 22.
      /You must provide an `updateQuery` function when using `fetchMore`/,
      /go\.apollo\.dev\/c\/err#.*message%22%3A22/,
    ],
  });
}

/**
 * Set the authenticated user context in Sentry.
 * Called from AuthContext when user data is available.
 */
export function setSentryUser(user: {
  id: string | number;
  email: string;
  name?: string | null;
  jobTitle?: string | null;
  objective?: string | null;
  createdAt?: string | null;
  role?: string;
  isAdmin?: boolean;
  plan?: string | null;
  subscription?: {
    status?: string;
    plan?: { name?: string; slug?: string } | null;
  } | null;
}) {
  if (!SENTRY_DSN) return;

  // Parse subscription/plan info
  let planName: string | undefined;
  let subscriptionStatus: string | undefined;

  if (user.subscription) {
    const sub = typeof user.subscription === 'string'
      ? (() => { try { return JSON.parse(user.subscription as string); } catch { return null; } })()
      : user.subscription;

    if (sub) {
      planName = sub.plan?.name || sub.plan?.slug || undefined;
      subscriptionStatus = sub.status || undefined;
    }
  }

  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.name || undefined,
    ...(user.jobTitle ? { job_title: user.jobTitle } : {}),
    ...(user.objective ? { objective: user.objective } : {}),
    ...(user.createdAt ? { created_at: user.createdAt } : {}),
  });

  if (user.role !== undefined) {
    Sentry.setTag('user.role', user.role || 'unknown');
  }
  if (user.isAdmin !== undefined) {
    Sentry.setTag('user.is_admin', String(user.isAdmin || false));
  }

  if (planName) {
    Sentry.setTag('user.plan', planName);
  }
  if (subscriptionStatus) {
    Sentry.setTag('user.subscription_status', subscriptionStatus);
  }

  const profile = {
    ...(user.jobTitle ? { job_title: user.jobTitle } : {}),
    ...(user.objective ? { objective: user.objective } : {}),
    ...(user.createdAt ? { created_at: user.createdAt } : {}),
    ...(planName ? { plan: planName } : {}),
    ...(subscriptionStatus ? { subscription_status: subscriptionStatus } : {}),
  };
  if (Object.keys(profile).length > 0) {
    Sentry.setContext('profile', profile);
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser() {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
  Sentry.setContext('profile', null);
}

export { Sentry };
